/**
 * Serialising the strategic layer.
 *
 * A campaign is a long-lived save — tens of hours, hundreds of turns — so this
 * matters more here than anywhere else in the project. The rule is the same one
 * `src/sim/save.ts` follows: the field list is exactly the field list
 * `hashCampaign` walks. If the two ever drift, a campaign will load into a
 * world that looks right and then quietly stops being the campaign that was
 * saved.
 *
 * Everything in `CampaignState` is plain JSON-safe data, so this is a deep copy
 * with a normalising pass on the way in. There are no typed arrays and no
 * handles to fix up, which is a deliberate property of the shape rather than a
 * happy accident.
 */

import { emptyResources, RESOURCE_KEYS, type ResourceBundle } from '../types.ts';
import { cloneCompanion } from '../systems/companions.ts';
import { NATIONS } from '../../data/nations.ts';
import { PROVINCES } from '../../data/provinces.ts';
import { createCampaign } from './state.ts';
import { CasusBelli, createDiplomacy, type DiplomacyState } from './diplotypes.ts';
import { loadTrade, saveTrade } from './tradesave.ts';
import {
  MAX_ORDER_UNITS,
  MAX_PRICE_TENTHS,
  OrderKind,
  type MarketState,
} from './markettypes.ts';
import { ESTATE_COUNT } from './estates.ts';
import { MAX_EVENT_HISTORY } from './chronicletypes.ts';
import { loadPendingEvent } from './chronicleturn.ts';
import { BattleKind, TurnPhase, type CampaignState, type Nation, type ProvinceState } from './types.ts';
import { MAX_VILLAGE_LEVEL, VILLAGE_PROFILES, villageTypeForProvince } from './villages.ts';

export type CampaignSave = CampaignState;

export function saveCampaign(c: CampaignState): CampaignSave {
  return {
    turn: c.turn,
    phase: c.phase,
    playerNation: c.playerNation,
    humanNations: [...c.humanNations],
    capital: c.capital,
    nations: c.nations.map((n) => ({
      def: n.def,
      treasury: { ...n.treasury },
      manpower: n.manpower,
      alive: n.alive,
      atWar: n.atWar.slice(),
      relations: n.relations.slice(),
      confession: n.confession,
      liege: n.liege,
      subject: n.subject,
      subjectSince: n.subjectSince,
      estates: n.estates.slice(),
    })),
    provinces: c.provinces.map((p) => ({ ...p, villageStock: { ...p.villageStock } })),
    links: c.links.map((l) => ({ ...l })),
    armies: c.armies.map((a) => ({ ...a, heroes: a.heroes.map(cloneCompanion), regiments: a.regiments.map((r) => ({ ...r })) })),
    nextArmyId: c.nextArmyId,
    fleets: c.fleets.map((f) => ({ ...f, cargo: f.cargo.slice() })),
    nextFleetId: c.nextFleetId,
    lanes: c.lanes.map((l) => ({ ...l })),
    camps: c.camps.map((k) => ({ ...k })),
    cities: c.cities.map((k) => ({ ...k, stock: { ...k.stock } })),
    warnings: c.warnings.map((wn) => ({ ...wn })),
    diplo: {
      wars: c.diplo.wars.map((x) => ({
        ...x,
        attackers: x.attackers.slice(),
        defenders: x.defenders.slice(),
      })),
      treaties: c.diplo.treaties.map((t) => ({ ...t })),
      truces: c.diplo.truces.map((t) => ({ ...t })),
      access: c.diplo.access.map((g) => ({ ...g })),
      offers: c.diplo.offers.map((o) => ({ ...o })),
      nextWarId: c.diplo.nextWarId,
      nextOfferId: c.diplo.nextOfferId,
    },
    trade: saveTrade(c.trade),
    // The whole book, escrow included: a sell order's goods exist nowhere but
    // on the order, so a save that dropped one would delete forty iron.
    market: {
      orders: c.market.orders.map((o) => ({ ...o })),
      nextOrderId: c.market.nextOrderId,
    },
    pending: c.pending ? { ...c.pending } : null,
    /**
     * The question on the table.
     *
     * A campaign saved with a question up has to load with the question up. It is
     * not a UI state — it is a frozen realm waiting on an answer — and dropping it
     * on load would resume a campaign the player never decided anything in. The
     * context is deep-copied with it, because that is what the answer applies to.
     */
    pendingEvent: c.pendingEvent
      ? { ...c.pendingEvent, choices: c.pendingEvent.choices.slice(), ctx: { ...c.pendingEvent.ctx } }
      : null,
    eventHistory: c.eventHistory.slice(),
    log: c.log.slice(),
    age: c.age,
    winner: c.winner,
    over: c.over,
  };
}

/**
 * Read one back. A save from an older build may be missing a field the campaign
 * has grown since; every one of those falls back to what a fresh campaign would
 * have, so an old save loads into a playable board rather than into `undefined`.
 */
export function loadCampaign(raw: CampaignSave | null | undefined): CampaignState | undefined {
  if (!raw) return undefined;
  const fresh = createCampaign('ashen');

  const nations = NATIONS.map((_, i) => {
    const s = raw.nations?.[i];
    const base = fresh.nations[i];
    return {
      def: s?.def ?? i,
      treasury: fillResources(s?.treasury),
      manpower: s?.manpower ?? 0,
      alive: s?.alive ?? true,
      atWar: sizedNumbers(s?.atWar, NATIONS.length, base.atWar),
      relations: sizedNumbers(s?.relations, NATIONS.length, base.relations),
      // A campaign saved before crowns had a faith wakes up keeping the one its
      // nation was always going to keep, which is what a fresh board would give it.
      confession: s?.confession ?? base.confession,
      liege: s?.liege ?? -1,
      subject: s?.subject ?? 0,
      subjectSince: s?.subjectSince ?? 0,
      /**
       * A campaign saved before there was anybody at home wakes with every estate
       * indifferent, which is exactly where a fresh board opens. `SAVE_VERSION` is
       * frozen by agreement; the `??` is the migration and it is idempotent.
       *
       * Sized rather than copied, so a save written by a build with a different
       * number of estates loads into this build's four rather than into a ragged
       * array that would hash a different length every month.
       */
      estates: sizedNumbers(s?.estates, ESTATE_COUNT, base.estates),
    };
  });

  const oldCities = Array.isArray(raw.cities) ? raw.cities : fresh.cities;
  const legacyCitySites = new Set(oldCities.map(city => city.at));
  legacyCitySites.add(raw.capital ?? fresh.capital);
  const provinces: ProvinceState[] = PROVINCES.map((_, i) => {
    const s = raw.provinces?.[i];
    const base = fresh.provinces[i];
    const holdingKind = s?.holdingKind === 'city' || s?.holdingKind === 'village' ? s.holdingKind
      : legacyCitySites.has(i) || (s?.tier ?? 1) >= 4 ? 'city' : 'village';
    const villageType = holdingKind === 'city' ? '' : VILLAGE_PROFILES.some(profile => profile.id === s?.villageType)
      ? s.villageType : villageTypeForProvince(i);
    return {
      holdingKind,
      villageType,
      villageStock: Object.fromEntries(RESOURCE_KEYS.filter(key => Number.isFinite(s?.villageStock?.[key]) && (s?.villageStock?.[key] ?? 0) > 0)
        .map(key => [key, s.villageStock[key]])),
      owner: s?.owner ?? base.owner,
      garrison: s?.garrison ?? base.garrison,
      fort: s?.fort ?? base.fort,
      unrest: s?.unrest ?? 0,
      development: s?.development ?? 1,
      // A campaign saved before the ladder existed wakes with every holding a
      // hamlet and nobody's masons anywhere, which is exactly where a fresh
      // board stands. `SAVE_VERSION` is frozen by agreement; the `??` is the
      // migration, and every line of it is idempotent.
      tier: Math.max(1, Math.min(holdingKind === 'village' ? MAX_VILLAGE_LEVEL : 10, s?.tier ?? base.tier)),
      worksTo: Math.max(0, Math.min(holdingKind === 'village' ? MAX_VILLAGE_LEVEL : 10, s?.worksTo ?? 0)),
      worksT: s?.worksT ?? 0,
      worksBy: s?.worksBy ?? -1,
      siegeBy: s?.siegeBy ?? -1,
      siegeT: s?.siegeT ?? 0,
      claimedBy: s?.claimedBy ?? -1,
      // A campaign saved before there were harbours wakes up with none, which
      // is exactly what a fresh board gives every province anyway.
      port: s?.port ?? 0,
      // A campaign saved before a holding's own works counted wakes with none
      // counted, which is exactly where a fresh board stands. The capital sync
      // writes the real number the first time the player walks out to the map.
      // Idempotent, per this file's standing rule; `SAVE_VERSION` is untouched.
      barracks: s?.barracks ?? 0,
      /**
       * Which of your holdings are towns you have stood in, and what is owed to
       * each of them.
       *
       * A campaign saved before a holding could be entered wakes with none
       * entered, and `base.settled` puts the one back that always was: the
       * player's own seat, whose settlement world such a save is already
       * carrying in the envelope beside it. Getting that wrong is not cosmetic —
       * a capital that read `settled: 0` would offer to found a second town on
       * the ground the player is standing on.
       *
       * `SAVE_VERSION` went to 8 for these two, and both lines are still
       * unconditional `??` defaults rather than a `version < 8` block, for the
       * reason this file states three times over: the hazard is a save stamped
       * with the *current* number by a build that has never heard of the field,
       * which is what every parallel worktree in this batch writes.
       */
      settled: s?.settled ?? 0,
      wreck: s?.wreck ?? 0,
    };
  });

  /**
   * …and the seat is one of them, whatever the columns say.
   *
   * Hoisted out of the literal below because the province table needs it. A
   * save written before holdings could be entered has `settled: 0` everywhere,
   * including on the capital — whose settlement world is sitting in the
   * envelope right beside it. Left at zero, the door would offer to *found* a
   * town on the ground the player is standing in. It is a `max` rather than a
   * write so a real turn number already recorded is never thrown away.
   */
  const capital = raw.capital ?? fresh.capital;
  if (provinces[capital]) provinces[capital].settled = Math.max(1, provinces[capital].settled);

  const links =
    Array.isArray(raw.links) && raw.links.length > 0
      ? raw.links.map((l) => ({ a: l.a, b: l.b, kind: l.kind, march: l.march }))
      : fresh.links;

  return {
    turn: raw.turn ?? 1,
    phase: raw.phase ?? TurnPhase.Orders,
    playerNation: raw.playerNation ?? 0,
    // A save from before rooms existed knows one crown, and it is the one it
    // was already calling the player's.
    humanNations: raw.humanNations ?? [raw.playerNation ?? 0],
    // A campaign saved before the capital was tracked falls back to the one its
    // nation starts from, which is where its city would have been anyway.
    capital,
    /**
     * A campaign saved before the realm had an Age wakes in the Ember, which is
     * where every campaign opens. The capital sync writes the real one the first
     * time the player walks out to the map, so nothing is lost for longer than a
     * month of play.
     */
    age: raw.age ?? 1,
    nations,
    provinces,
    links,
    armies: (raw.armies ?? []).map((a) => ({
      id: a.id,
      heroes: (a.heroes ?? []).map((h) => ({ ...cloneCompanion(h), armyId: a.id, unitH: -1, companyId: -1 })),
      owner: a.owner,
      at: a.at,
      moveTo: a.moveTo ?? -1,
      moveT: a.moveT ?? 0,
      // A save from before marches were lanes has no origin recorded. Falling
      // back to where the army stands is exactly right: that *is* where it set
      // out from, and the only thing lost is how far along it already was.
      moveFrom: a.moveFrom ?? (a.moveTo >= 0 ? a.at : -1),
      moveTotal: a.moveTotal ?? Math.max(a.moveT ?? 0, 0),
      dest: a.dest ?? -1,
      intercepting: a.intercepting ?? -1,
      // A host from before the running fight was fixed is a host that is not
      // regrouping — which is the only state such a save can have been in, since
      // nothing else on the record could have put it in another. Idempotent
      // against a save that already carries it.
      regroup: a.regroup ?? 0,
      regiments: (a.regiments ?? []).map((r) => ({ typeIdx: r.typeIdx, models: r.models })),
      stance: a.stance ?? 0,
      siegeTurns: a.siegeTurns ?? 0,
      supply: a.supply ?? 1,
      victories: a.victories ?? 0,
      objective: a.objective ?? -1,
      idleTurns: a.idleTurns ?? 0,
      // A host from before there were ships is a host standing on the ground.
      aboard: a.aboard ?? -1,
      // And a host from before borders meant anything is standing somewhere it
      // was entitled to stand. Zero is what a fresh host carries; the council's
      // first pass re-derives it from the board on the very next month, so an
      // old save that genuinely had an army squatting abroad starts its grace
      // period rather than being killed for something it did before the rule.
      trespassT: a.trespassT ?? 0,
      // A host from before there was anywhere to hide is standing in the open,
      // which is where every host has always been. Idempotent.
      sheltered: a.sheltered ?? false,
      name: a.name ?? 'Host',
    })),
    nextArmyId: raw.nextArmyId ?? 1,
    fleets: (raw.fleets ?? []).map((f) => ({
      id: f.id,
      owner: f.owner,
      at: f.at,
      moveTo: f.moveTo ?? -1,
      moveFrom: f.moveFrom ?? -1,
      moveT: f.moveT ?? 0,
      moveTotal: f.moveTotal ?? Math.max(f.moveT ?? 0, 0),
      intercepting: f.intercepting ?? -1,
      ships: f.ships ?? 0,
      cargo: (f.cargo ?? []).slice(),
      name: f.name ?? 'Fleet',
    })),
    nextFleetId: raw.nextFleetId ?? 1,
    /**
     * The lane network, from the save when it has one and from the realm when it
     * does not.
     *
     * Same shape as `links` above and the same argument: a campaign saved before
     * the sea existed has no lanes recorded, and rebuilding them from the
     * generated realm gives it exactly the network a fresh campaign on the same
     * seed would have. A campaign saved *after* keeps whatever it had, which is
     * what will matter the first time anything is able to close one.
     */
    lanes:
      Array.isArray(raw.lanes) && raw.lanes.length > 0
        ? raw.lanes.map((l) => ({ a: l.a, b: l.b, months: l.months }))
        : fresh.lanes,
    camps: (raw.camps ?? []).map((k) => ({
      at: k.at,
      strength: k.strength ?? 0,
      founded: k.founded ?? 1,
      bounty: k.bounty ?? 0,
    })),
    /**
     * The warehouses. A save from before cities existed gets the ones a fresh
     * board would have — a yard at every crown's seat, empty — rather than
     * none, because a realm with no city anywhere can never build anything and
     * would load as a permanently crippled campaign rather than an old one.
     *
     * Sorted on the way in for the same reason `createCampaign` sorts: every
     * walk of this list has to be stable or the hash is not.
     */
    cities: [...new Set([...oldCities.map(city => city.at), ...provinces.flatMap((prov, at) =>
      !raw.provinces?.[at]?.holdingKind && prov.holdingKind === 'city' ? [at] : [])])].flatMap(at => {
      const prov = provinces[at];
      if (!prov || prov.holdingKind !== 'city') return [];
      const k = oldCities.find(city => city.at === at);
      return [{ at, owner: k?.owner ?? prov.owner, stock: fillResources(k?.stock), founded: k?.founded ?? 1 }];
    })
      .sort((a, b) => a.at - b.at),
    warnings: (raw.warnings ?? []).map((wn) => ({
      kind: wn.kind ?? 0,
      nation: wn.nation ?? -1,
      armyId: wn.armyId ?? -1,
      from: wn.from ?? -1,
      to: wn.to ?? -1,
      turns: wn.turns ?? 0,
      strength: wn.strength ?? 0,
      sinceTurn: wn.sinceTurn ?? (raw.turn ?? 1),
    })),
    diplo: loadDiplomacy(raw, nations),
    // A campaign from before trade existed gets a freshly grown graph with
    // nobody's merchants on it. `SAVE_VERSION` is untouched, per the rule in
    // COORDINATION.md: the `??` defaults in `loadTrade` are the migration, and
    // every one of them is idempotent.
    trade: loadTrade(raw.trade, fresh.trade, NATIONS.length),
    /**
     * The market's book. A campaign saved before the market existed wakes with
     * an empty book and the counter at 1 — exactly where a fresh board opens —
     * so `SAVE_VERSION` is untouched, per the standing rule: the `??`s are the
     * migration and every one is idempotent. An order naming a resource this
     * build has never heard of is dropped rather than loaded, the same
     * treatment a merchant whose node no longer exists gets — its escrow came
     * from a table that no longer describes anything, so there is nothing
     * honest to refund it into.
     */
    market: loadMarket(raw.market),
    // A pending battle from before there were three kinds of them is a fight
    // for a province, which is the only kind that existed.
    pending: raw.pending
      ? {
          ...raw.pending,
          kind: raw.pending.kind ?? BattleKind.Province,
          roadTo: raw.pending.roadTo ?? -1,
          interceptor: raw.pending.interceptor ?? -1,
        }
      : null,
    /**
     * A campaign saved before the realm asked anybody anything wakes with nothing
     * on the table and no memory, which is where a fresh board opens. Both `??`s
     * are idempotent and `SAVE_VERSION` is untouched.
     *
     * `loadPendingEvent` fills every slot of the frozen context rather than
     * spreading it, so a question written by a build with fewer context slots
     * loads with the missing ones at -1 instead of `undefined` — which would
     * otherwise reach `h.i32` and hash as `NaN`.
     */
    pendingEvent: loadPendingEvent(raw.pendingEvent),
    eventHistory: (raw.eventHistory ?? []).slice(-MAX_EVENT_HISTORY),
    log: (raw.log ?? []).slice(),
    winner: raw.winner ?? -1,
    over: raw.over ?? false,
  };
}

/**
 * Diplomacy, out of a save that may predate it entirely.
 *
 * The interesting case is the one in the middle. A campaign saved before this
 * system existed carries an `atWar` matrix in which *everybody* is at war with
 * everybody, because that was the opening position and nothing could ever leave
 * it. Loading that into an empty war list would silently declare universal peace
 * — a nicer board, arriving from nowhere, on a save the player thought they
 * understood. Loading it and leaving the matrix alone would be worse: wars that
 * exist in the index and nowhere else can never be negotiated and never end.
 *
 * So it is reconstructed. Every at-war pair becomes a real war, attacker being
 * the lower index, declared with no pretext, at zero score. That is a defensible
 * reading of what the old board actually meant, and — crucially — every one of
 * those wars can now be brought to a peace.
 */
function loadDiplomacy(raw: CampaignSave, nations: Nation[]): DiplomacyState {
  const src = raw.diplo as DiplomacyState | undefined;
  if (src && Array.isArray(src.wars)) {
    return {
      wars: src.wars.map((x) => ({
        id: x.id,
        attacker: x.attacker,
        defender: x.defender,
        attackers: (x.attackers ?? [x.attacker]).slice(),
        defenders: (x.defenders ?? [x.defender]).slice(),
        cb: x.cb ?? 0,
        goal: x.goal ?? -1,
        startTurn: x.startTurn ?? 1,
        score: x.score ?? 0,
        battlesWon: x.battlesWon ?? 0,
        battlesLost: x.battlesLost ?? 0,
        taken: x.taken ?? 0,
        lost: x.lost ?? 0,
        lastOffer: x.lastOffer ?? -99,
      })),
      treaties: (src.treaties ?? []).map((t) => ({
        kind: t.kind,
        a: t.a,
        b: t.b,
        since: t.since ?? 1,
      })),
      truces: (src.truces ?? []).map((t) => ({ a: t.a, b: t.b, until: t.until ?? 0 })),
      /**
       * A campaign saved before passage existed wakes with no grants standing,
       * which is exactly the position a fresh board opens in: every border shut
       * and every crown free to ask. `SAVE_VERSION` is frozen by agreement — the
       * `??` is the migration, and it is idempotent.
       */
      access: (src.access ?? [])
        .map((g) => ({ from: g.from, to: g.to, since: g.since ?? 1 }))
        .sort((x, y) => x.from - y.from || x.to - y.to),
      offers: (src.offers ?? []).map((o) => ({ ...o })),
      nextWarId: src.nextWarId ?? 1,
      nextOfferId: src.nextOfferId ?? 1,
    };
  }

  const out = createDiplomacy();
  const turn = raw.turn ?? 1;
  for (let a = 0; a < nations.length; a++) {
    for (let b = a + 1; b < nations.length; b++) {
      if (nations[a].atWar[b] !== 1) continue;
      out.wars.push({
        id: out.nextWarId++,
        attacker: a,
        defender: b,
        attackers: [a],
        defenders: [b],
        cb: CasusBelli.None,
        goal: -1,
        startTurn: turn,
        score: 0,
        battlesWon: 0,
        battlesLost: 0,
        taken: 0,
        lost: 0,
        lastOffer: -99,
      });
    }
  }
  return out;
}

/**
 * The order book, out of a save that may predate it. Amounts and prices are
 * re-clamped to the integers every write keeps them at, so a hand-edited or
 * truncated save cannot smuggle a float into hashed state.
 */
function loadMarket(raw: MarketState | undefined): MarketState {
  const orders = (raw?.orders ?? [])
    .filter((o) => (RESOURCE_KEYS as readonly string[]).includes(o.key))
    // A malformed kind is dropped, never coerced: a truncated buy order read
    // as a sell would be an ask whose escrow no treasury ever paid — matching
    // it mints goods from nothing. An order the save cannot vouch for is not
    // an order.
    .filter((o) => o.kind === OrderKind.Buy || o.kind === OrderKind.Sell)
    .filter((o) => Number.isFinite(o.id) && Number.isFinite(o.amount) && Number.isFinite(o.price))
    .map((o) => ({
      id: o.id,
      nation: o.nation ?? -1,
      kind: o.kind,
      key: o.key,
      amount: Math.max(0, Math.min(MAX_ORDER_UNITS, Math.round(o.amount))),
      price: Math.max(1, Math.min(MAX_PRICE_TENTHS, Math.round(o.price))),
      placedTurn: o.placedTurn ?? 1,
    }));
  return { orders, nextOrderId: raw?.nextOrderId ?? 1 };
}

function fillResources(r: Partial<ResourceBundle> | undefined): ResourceBundle {
  const out = emptyResources();
  if (!r) return out;
  for (const k of RESOURCE_KEYS) out[k] = r[k] ?? 0;
  return out;
}

function sizedNumbers(src: number[] | undefined, n: number, fallback: number[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < n; i++) out.push(src?.[i] ?? fallback[i] ?? 0);
  return out;
}
